import { env } from "@/lib/env"

/**
 * Centralized, secure logging utility
 * Provides consistent logging behavior across the application
 */

/**
 * Safe logging function that respects environment settings
 * In production, sensitive data is filtered and error details are minimized
 */
function createLogger(prefix?: string) {
  const logPrefix = prefix ? `[${prefix}]` : ""

  return {
    debug: (message: string, data?: unknown) => {
      if (env.isDevelopment) {
        globalThis.console.debug(`${logPrefix} ${message}`, data || "")
      }
    },

    info: (message: string, data?: unknown) => {
      if (env.isDevelopment) {
        globalThis.console.info(`${logPrefix} ${message}`, data || "")
      }
    },

    warn: (message: string, data?: unknown) => {
      if (env.isDevelopment) {
        globalThis.console.warn(`${logPrefix} ${message}`, data || "")
      } else {
        // In production, log warnings but without sensitive data
        globalThis.console.warn(`${logPrefix} ${message}`)
      }
    },

    error: (
      message: string,
      error?: Error | unknown,
      sensitiveData?: unknown,
    ) => {
      if (env.isDevelopment) {
        // In development, log full details
        globalThis.console.error(`${logPrefix} ${message}`, {
          error,
          data: sensitiveData,
        })
      } else {
        // In production, log minimal error info without sensitive details
        if (error instanceof Error) {
          globalThis.console.error(`${logPrefix} ${message}: ${error.name}`)
        } else {
          globalThis.console.error(`${logPrefix} ${message}`)
        }
      }
    },

    // Special method for form/user input errors
    formError: (message: string, error?: Error | unknown) => {
      if (env.isDevelopment) {
        globalThis.console.error(`${logPrefix} Form Error: ${message}`, error)
      } else {
        // In production, log generic form error without details
        globalThis.console.error(`${logPrefix} Form validation failed`)
      }
    },

    // Special method for authentication errors
    authError: (message: string, error?: Error | unknown) => {
      if (env.isDevelopment) {
        globalThis.console.error(`${logPrefix} Auth Error: ${message}`, error)
      } else {
        // In production, log minimal auth error
        globalThis.console.error(`${logPrefix} Authentication failed`)
      }
    },
  }
}

// Export common loggers
export const logger = createLogger()
export const authLogger = createLogger("auth")
export const formLogger = createLogger("form")
export const storageLogger = createLogger("storage")

// Export the factory for custom loggers
export { createLogger }

/**
 * Legacy console wrapper for gradual migration
 * @deprecated Use specific loggers instead
 */
export const consoleWrapper = {
  ...globalThis.console,
  error: (message: string, ...args: unknown[]) => {
    logger.error(message, args.length > 0 ? args[0] : undefined, args.slice(1))
  },
  warn: (message: string, ...args: unknown[]) => {
    logger.warn(message, args.length > 0 ? args[0] : undefined)
  },
  debug: (message: string, ...args: unknown[]) => {
    logger.debug(message, args.length > 0 ? args[0] : undefined)
  },
  log: (message: string, ...args: unknown[]) => {
    logger.info(message, args.length > 0 ? args[0] : undefined)
  },
}
